import { AuthService, PasswordValidation } from '@geovelo-frontends/commons';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { red } from '@mui/material/colors';
import { FormikHelpers, useFormik } from 'formik';
import { Link, graphql, navigate } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import * as Yup from 'yup';

import { SignUpQuery } from '../../../graphql-types';
import Button from '../../components/button';
import { VisibilityIcon, VisibilityOffIcon } from '../../components/icons';
import Seo from '../../components/seo';
import { AppContext } from '../../context';
import { TAppLanguage, defaultLanguage } from '../../languages';
import SignInLayout from '../../layouts/sign-in';
import { TPageProps } from '../../page-props';

interface IFormValues {
  email: string;
  password: string;
  passwordConfirmation: string;
  approveGCU: boolean;
  approvePrivacyPolicy: boolean;
}

function SignUpPage(props: TPageProps<SignUpQuery>) {
  const [passwordShowed, showPassword] = useState(false);
  const {
    actions: { setCurrentUser },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { isSubmitting, values, touched, errors, isValid, handleChange, handleSubmit } =
    useFormik<IFormValues>({
      initialValues: {
        email: '',
        password: '',
        passwordConfirmation: '',
        approveGCU: false,
        approvePrivacyPolicy: false,
      },
      validationSchema: Yup.object().shape({
        email: Yup.string().required(t('commons.sign_in_form.email_required') || ''),
        password: Yup.string()
          .required(t('commons.sign_up_form.password_pattern') || '')
          .matches(
            /^(?=.*[\d])(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_]).{8,}$/g,
            t('commons.sign_up_form.password_pattern') || '',
          ),
        passwordConfirmation: Yup.string().when('password', {
          is: (password: string) => password,
          then: (shema) =>
            shema
              .oneOf([Yup.ref('password')], t('commons.sign_up_form.passwords_must_match') || '')
              .required(t('commons.sign_up_form.passwords_must_match') || ''),
        }),
        approveGCU: Yup.boolean().oneOf([true]),
        approvePrivacyPolicy: Yup.boolean().oneOf([true]),
      }),
      enableReinitialize: true,
      onSubmit,
    });
  const snackbar = useSnackbar();

  useEffect(() => {
    if (!props.data.prismicSignUp)
      navigate(`/${defaultLanguage}${props.pageContext.slug.substring(3)}`);
  }, []);

  async function onSubmit(
    { email, password }: IFormValues,
    { setSubmitting, setErrors }: FormikHelpers<IFormValues>,
  ) {
    setSubmitting(true);

    try {
      const user = await AuthService.register(email, password, currentLanguage as TAppLanguage);

      setCurrentUser(user);
    } catch (err) {
      if (err && typeof err === 'object' && (err as { code?: number }).code === 409) {
        setErrors({ email: t('commons.sign_up_form.email_already_exists') || '' });
      } else {
        snackbar?.enqueueSnackbar(t('commons.sign_up_form.server_error') || '');
      }
      setSubmitting(false);
    }
  }

  const title = props.data.prismicSignUp?.data.title?.text;

  return (
    <>
      <Seo title={`${title || t('geovelo.pages_titles.sign_up')}`} {...props} />
      <SignInLayout
        signUp
        data={props.data.prismicSignUp?.data}
        titleKey="geovelo.navigation.sign_up"
      >
        <Box component="form" display="flex" flexDirection="column" gap={2} onSubmit={handleSubmit}>
          <StyledTextField
            disabled={isSubmitting}
            error={Boolean(errors.email && touched.email)}
            helperText={touched.email && errors.email}
            id="email"
            margin="none"
            name="email"
            onChange={handleChange}
            placeholder={t('commons.sign_in_form.email') || ''}
            size="small"
            type="email"
            value={values.email}
            variant="filled"
          />
          <StyledTextField
            disabled={isSubmitting}
            error={Boolean(errors.password && touched.password)}
            id="password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => showPassword(!passwordShowed)}
                    size="small"
                  >
                    {passwordShowed ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            margin="none"
            name="password"
            onChange={handleChange}
            placeholder={t('commons.sign_in_form.password') || ''}
            size="small"
            type={passwordShowed ? 'text' : 'password'}
            value={values.password}
            variant="filled"
          />
          <PasswordValidation password={values.password} />
          <StyledTextField
            disabled={isSubmitting}
            error={Boolean(errors.passwordConfirmation && touched.passwordConfirmation)}
            helperText={touched.passwordConfirmation && errors.passwordConfirmation}
            id="passwordConfirmation"
            margin="none"
            name="passwordConfirmation"
            onChange={handleChange}
            placeholder={t('commons.sign_up_form.password_confirmation') || ''}
            size="small"
            type="password"
            value={values.passwordConfirmation}
            variant="filled"
          />
          <Box display="flex" flexDirection="column">
            <FormControl component="fieldset" disabled={isSubmitting} margin="none">
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    name="approveGCU"
                    onChange={handleChange}
                    style={errors.approveGCU && touched.approveGCU ? { color: red[500] } : {}}
                    value={values.approveGCU}
                  />
                }
                label={
                  <Typography variant="caption">
                    <Trans
                      components={[
                        <Typography
                          color="primary"
                          component={Link}
                          key={0}
                          to={`/${currentLanguage}/eula`}
                          variant="caption"
                        />,
                      ]}
                      i18nKey="commons.sign_up_form.accept_gcu"
                    />
                  </Typography>
                }
              />
            </FormControl>
            <FormControl component="fieldset" disabled={isSubmitting} margin="none">
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    name="approvePrivacyPolicy"
                    onChange={handleChange}
                    style={
                      errors.approvePrivacyPolicy && touched.approvePrivacyPolicy
                        ? { color: red[500] }
                        : {}
                    }
                    value={values.approvePrivacyPolicy}
                  />
                }
                label={
                  <Typography variant="caption">
                    <Trans
                      components={[
                        <Typography
                          color="primary"
                          component={Link}
                          key={0}
                          to={`/${currentLanguage}/privacy-policy`}
                          variant="caption"
                        />,
                      ]}
                      i18nKey="commons.sign_up_form.accept_privacy_policy"
                    />
                  </Typography>
                }
              />
            </FormControl>
          </Box>
          <Button
            color="primary"
            disabled={isSubmitting || !isValid || !values.email}
            size="medium"
            type="submit"
            variant="contained"
          >
            {t('commons.actions.sign_up')}
          </Button>
        </Box>
        <Box alignItems="center" display="flex" flexDirection="row" gap={1}>
          <Typography align="center" color="textSecondary" component="p" variant="caption">
            {t('commons.sign_in_dialog.already_registered')}
          </Typography>
          <Button
            color="primary"
            component={Link}
            size="small"
            state={props.location.state}
            to={`/${currentLanguage}/sign-in${props.location.search}`}
            variant="text"
          >
            {t('geovelo.navigation.sign_in')}
          </Button>
        </Box>
      </SignInLayout>
    </>
  );
}

const StyledTextField = styled(TextField)`
  &&& {
    .MuiInputBase-root {
      &::before,
      &::after {
        border-bottom: 0 !important;
      }

      border-radius: 8px;

      input {
        padding-bottom: 12px;
        padding-top: 12px;
      }
    }
  }
`;

export const query = graphql`
  fragment SignUpData on PrismicSignUpData {
    title {
      text
    }
    form_title {
      text
    }
    image {
      gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH)
    }
  }
`;

export default SignUpPage;
